import { RowData } from "../../Interfaces/interfaces";

const MOONBUG_CLAIM_OWNERS = new Set([
  "Moonbug Entertainment",
  "Moonbug Entertainment Music",
]);

export const parseClaimsArray = (claimsData: unknown): any[] => {
  if (!claimsData || claimsData === "null" || claimsData === "undefined") {
    return [];
  }
  if (Array.isArray(claimsData)) {
    return claimsData;
  }
  if (typeof claimsData === "string") {
    const trimmed = claimsData.trim();
    if (
      trimmed === "" ||
      trimmed === "[]" ||
      trimmed === "null" ||
      trimmed === "undefined"
    ) {
      return [];
    }
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeOwnerName = (owner: unknown): string =>
  owner == null ? "" : String(owner).trim();

const claimHasNonMoonbugOwner = (claim: any): boolean => {
  const owners = claim?.ownerName;
  const ownerList = Array.isArray(owners)
    ? owners
    : owners != null
    ? [owners]
    : [];
  return ownerList.some((owner) => {
    const name = normalizeOwnerName(owner);
    return name !== "" && !MOONBUG_CLAIM_OWNERS.has(name);
  });
};

/** YouTube Partner claim statuses we treat as currently in force. */
export const isClaimActive = (claim: any): boolean => {
  const status = String(claim?.claimStatus ?? claim?.status ?? "")
    .trim()
    .toLowerCase();
  return status === "active";
};

export const hasNonMoonbugThirdPartyClaimsInJson = (
  claimsData: unknown
): boolean =>
  parseClaimsArray(claimsData).some(
    (claim) => isClaimActive(claim) && claimHasNonMoonbugOwner(claim)
  );

const uniqueStrings = (values: string[]): string[] =>
  Array.from(new Set(values));

/** Content ID thirdPartyClaims only. Empty claims are not an issue. */
export const isThirdPartyClaimPresent = (row: RowData): boolean => {
  if (row.thirdPartyClaim === "Whitelisted") return false;
  return hasNonMoonbugThirdPartyClaimsInJson(row.thirdPartyClaims);
};

const claimHasMoonbugOwner = (claim: any): boolean => {
  const owners = claim?.ownerName;
  const ownerList = Array.isArray(owners)
    ? owners
    : owners != null
    ? [owners]
    : [];
  return ownerList.some((owner) => {
    const name = normalizeOwnerName(owner);
    return name !== "" && MOONBUG_CLAIM_OWNERS.has(name);
  });
};

const isDirectAssetId = (assetId: unknown): boolean => {
  if (assetId === null || assetId === undefined) return false;
  const value = String(assetId).trim();
  return (
    value !== "" &&
    value !== "Missing" &&
    value !== "null" &&
    value !== "undefined"
  );
};

const extractClaimsWithAssetIds = (
  claimsData: unknown
): Array<{ assetId: string; claim: any }> =>
  parseClaimsArray(claimsData)
    .map((claim) => ({
      assetId:
        typeof claim?.assetId === "string" ? claim.assetId.trim() : "",
      claim,
    }))
    .filter((entry) => entry.assetId !== "");

export const resolveScorecardAssetId = (row: RowData): string => {
  if (isDirectAssetId(row.assetId)) {
    return String(row.assetId).trim();
  }

  const claimEntries = [
    ...extractClaimsWithAssetIds(row.moonbugClaims),
    ...extractClaimsWithAssetIds(row.thirdPartyClaims),
    ...extractClaimsWithAssetIds(row.syncThirdPartyClaims),
  ];

  const moonbugAssetIds = uniqueStrings(
    claimEntries
      .filter((entry) => claimHasMoonbugOwner(entry.claim))
      .map((entry) => entry.assetId)
  );
  if (moonbugAssetIds.length > 0) {
    return moonbugAssetIds.join(", ");
  }

  const anyAssetIds = uniqueStrings(
    claimEntries.map((entry) => entry.assetId)
  );
  if (anyAssetIds.length > 0) {
    return anyAssetIds.join(", ");
  }

  return "Missing";
};
